import type { ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/features/auth/AuthProvider";
import type { RoleName } from "@/types/domain";

/**
 * Permission-based UI gate. Hides children when the current user lacks the
 * given permission. Backend remains the final authority on every request.
 */
export function PermissionGate({
  permission,
  fallback = null,
  children,
}: {
  permission: string;
  fallback?: ReactNode;
  children: ReactNode;
}) {
  const { can } = useAuth();
  return can(permission) ? <>{children}</> : <>{fallback}</>;
}

export function RoleGate({
  roles,
  fallback = null,
  children,
}: {
  roles: RoleName[];
  fallback?: ReactNode;
  children: ReactNode;
}) {
  const { hasAnyRole } = useAuth();
  return hasAnyRole(roles) ? <>{children}</> : <>{fallback}</>;
}

export function RequireAuth({ children }: { children: ReactNode }) {
  const { status } = useAuth();
  const location = useLocation();
  if (status === "loading") return <FullPageSpinner />;
  if (status === "anonymous") {
    const next = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/auth/login?next=${next}`} replace />;
  }
  return <>{children}</>;
}

export function RequireRole({ roles, children }: { roles: RoleName[]; children: ReactNode }) {
  const { status, hasAnyRole } = useAuth();
  if (status === "loading") return <FullPageSpinner />;
  if (status === "anonymous") return <Navigate to="/auth/login" replace />;
  if (!hasAnyRole(roles)) return <Navigate to="/access-denied" replace />;
  return <>{children}</>;
}

function FullPageSpinner() {
  return (
    <div className="min-h-dvh bg-off-white px-4 py-24" role="status" aria-live="polite">
      <span className="sr-only">Loading access information</span>
      <div aria-hidden className="mx-auto max-w-6xl animate-pulse space-y-6">
        <div className="h-12 w-64 rounded bg-slate-200" />
        <div className="grid gap-5 md:grid-cols-3">
          {[0, 1, 2].map((item) => (
            <div key={item} className="h-56 rounded-md bg-slate-200" />
          ))}
        </div>
      </div>
    </div>
  );
}
